import { useEffect, useState } from 'react'
import {
  feedbackFilters,
  feedbackQuery,
  type FeedbackReviewFilters,
} from './feedbackReviewSession.ts'

/**
 * Where you are, as a URL.
 *
 * This app used to keep the open verification in React state alone, which meant an
 * verification had no address: a reload lost it, the back button left the app, and a
 * simulation that redirected to its verification could not be returned to once the tab
 * moved on. Work that takes minutes to run needs somewhere to point at while
 * it runs.
 *
 * Hand-rolled rather than a router dependency: five routes, no nested layouts,
 * no data loaders. The History API is the whole mechanism. Deep links work
 * because both servers already fall back to index.html -- vite's dev server by
 * default, and the API's `serveStatic` catch-all in production.
 *
 * Paths are English like the rest of the code, while what renders on them is
 * Portuguese like the rest of the interface.
 */
/**
 * Where a verification lives, in the two forms an address can take.
 *
 * `numbered` is the one people use: the branch and the quotation number are
 * what a pharmacy says out loud, recognises in a chat message, and can type
 * when a prescription is no longer where they expected it. `identified` is the
 * uuid, which is the identity everything inside the system runs on and is what
 * every link shared before this existed still carries.
 *
 * Both are kept, and only one is ever produced: `toPath` writes the numbered
 * form, so a uuid address survives being opened and is not handed out again.
 */
export type VerificationAt =
  | { readonly kind: 'numbered'; readonly branch: number; readonly quotation: number }
  | { readonly kind: 'identified'; readonly id: string }

export type Route =
  | { readonly name: 'queue' }
  /**
   * An verification *is* the guided pass, as in V1. The full side-by-side panel is
   * the opt-in underneath it, not the other way round: a pharmacist opening an
   * verification is opening the thing that walks them through it.
   */
  | {
      readonly name: 'verification'
      readonly at: VerificationAt
      /**
       * A finding to open on, by fingerprint: its step, its row pinned. For a
       * link that has to show one case -- a feedback in the acompanhamento --
       * rather than the conferência from the top. `?apontamento=` in the URL.
       */
      readonly finding?: string | undefined
      /**
       * Where that finding was, for when a re-run has changed it: its pote and
       * its check. The fingerprint is a hash of the finding's content, so it
       * survives a re-run that says the same thing and not one that does not.
       * `?pote=` and `?tipo=`.
       */
      readonly near?:
        | { readonly preparation?: string | undefined; readonly check?: string | undefined }
        | undefined
    }
  | { readonly name: 'full'; readonly at: VerificationAt }
  | { readonly name: 'synonyms' }
  /** The catalogue, browsable. Where a product is looked up rather than ranked. */
  | { readonly name: 'products' }
  /** One catalogue product and its terms. Keyed by the ERP's own code. */
  | { readonly name: 'product'; readonly erpCode: string }
  | { readonly name: 'simulations' }
  /** The golden set: the labelled cases the reader is measured against. */
  | { readonly name: 'goldenSet' }
  /** Usuários: who is a member here, and who has been invited. Admins. */
  | { readonly name: 'users' }
  /** One member: their role and Formula Certa user. */
  | { readonly name: 'user'; readonly id: string }
  /** Inviting somebody in. */
  | { readonly name: 'userInvite' }
  /** Funções: which conferências each person here sees (#280). Admins. */
  | { readonly name: 'roles' }
  /** One role, or a new one when `id` is absent (super admins create). */
  | { readonly name: 'role'; readonly id?: string | undefined }
  /** The scheduled work, and the buttons that start it. */
  | { readonly name: 'jobs' }
  /** Every customer's filiais, read only. */
  | { readonly name: 'branches' }
  | { readonly name: 'branch'; readonly id: string }
  /** Every customer, and per customer which operational rules run. */
  | { readonly name: 'accounts' }
  /** One customer, a tab per part of it (#688). */
  | { readonly name: 'account'; readonly id: string; readonly tab: AccountTab }
  /** How one account compares pharmaceutical forms: its names and equivalences (#607). */
  | { readonly name: 'forms' }
  /** The forms this account makes in a prescribed one's place. */
  | { readonly name: 'formEquivalences' }
  /** One form and every name it is found by, keyed by our form id (`tablet`). */
  | { readonly name: 'form'; readonly id: string }
  /** What is up, and how quickly it answered. */
  | { readonly name: 'status' }
  | { readonly name: 'ai' }
  /**
   * Relatórios (#551): one screen, a tab per report. Super admin only.
   *
   * The tab is in the path, so a report has an address of its own to reload
   * and to send.
   */
  | { readonly name: 'reports'; readonly tab: ReportTab }
  | { readonly name: 'goldenCase'; readonly caseId: string }
  /**
   * The resolution golden set: the answer key for whether a pharmacist picked
   * the right product.
   *
   * Its own path rather than a tab held in React state, so a reload and a
   * shared link both land on the set somebody meant. `/golden-set/:id` is
   * already a reading case, so these cannot nest under it.
   */
  | { readonly name: 'resolutionSet' }
  | { readonly name: 'resolutionCase'; readonly caseId: string }
  /**
   * One simulated quotation being edited, addressed by its upload id.
   *
   * A page of its own so the edit survives a reload and can be returned to,
   * and so the index's chrome is not in the way of building a divergence.
   */
  | { readonly name: 'simulation'; readonly uploadId: string }
  /**
   * Acompanhamento de feedbacks (#405): the pharmacists' feedback as it
   * arrives, with the false positives and negatives counted in its header.
   */
  | {
      readonly name: 'feedback'
      readonly filters?: FeedbackReviewFilters | undefined
      readonly page?: number | undefined
    }
  | {
      readonly name: 'feedbackReview'
      readonly eventId: string
      readonly filters?: FeedbackReviewFilters | undefined
      readonly horizon?: string | undefined
      readonly queuePage?: number | undefined
    }

/**
 * The reports, in the order their tabs show. The first is where `/reports`
 * lands. Each value is also the tab's path segment.
 */
export const REPORT_TABS = ['findings', 'conference-time'] as const

export type ReportTab = (typeof REPORT_TABS)[number]

function toReportTab(part: string | undefined): ReportTab {
  return REPORT_TABS.find((tab) => tab === part) ?? REPORT_TABS[0]
}

/**
 * An account's page, in the order its tabs show (#688). Each value is also the
 * tab's path segment. The first is where `/accounts/<id>` opens, and its
 * address stays that one, so a link kept from before the tabs lands where it
 * did.
 */
export const ACCOUNT_TABS = [
  'details',
  'rules',
  'connection',
  'stages',
  'read-window',
  'erp-codes',
] as const

export type AccountTab = (typeof ACCOUNT_TABS)[number]

function toAccountTab(part: string | undefined): AccountTab {
  return ACCOUNT_TABS.find((tab) => tab === part) ?? ACCOUNT_TABS[0]
}

export function toPath(route: Route): string {
  switch (route.name) {
    case 'verification':
      return `${verificationPath(route.at)}${findingQuery(route.finding, route.near)}`
    case 'full':
      return `${verificationPath(route.at)}/full`
    case 'synonyms':
      return '/synonyms'
    case 'products':
      return '/products'
    case 'product':
      return `/products/${encodeURIComponent(route.erpCode)}`
    case 'simulations':
      return '/simulations'
    case 'goldenSet':
      return '/golden-set'
    case 'users':
      return '/users'
    case 'user':
      return `/users/${encodeURIComponent(route.id)}`
    case 'userInvite':
      return '/users/invite'
    case 'roles':
      return '/roles'
    case 'role':
      return route.id === undefined ? '/roles/new' : `/roles/${encodeURIComponent(route.id)}`
    case 'jobs':
      return '/jobs'
    case 'branches':
      return '/branches'
    case 'branch':
      return `/branches/${encodeURIComponent(route.id)}`
    case 'accounts':
      return '/accounts'
    case 'account':
      return route.tab === ACCOUNT_TABS[0]
        ? `/accounts/${encodeURIComponent(route.id)}`
        : `/accounts/${encodeURIComponent(route.id)}/${route.tab}`
    case 'forms':
      return '/forms'
    case 'formEquivalences':
      return '/forms/equivalences'
    case 'form':
      return `/forms/${encodeURIComponent(route.id)}`
    case 'status':
      return '/status'
    case 'ai':
      return '/ai'
    case 'reports':
      return `/reports/${route.tab}`
    case 'goldenCase':
      return `/golden-set/${encodeURIComponent(route.caseId)}`
    case 'resolutionSet':
      return '/resolution-set'
    case 'resolutionCase':
      return `/resolution-set/${encodeURIComponent(route.caseId)}`
    case 'simulation':
      return `/simulations/${encodeURIComponent(route.uploadId)}`
    case 'feedback':
    case 'feedbackReview': {
      const query = feedbackQuery(route.filters)
      if (route.name === 'feedback' && route.page !== undefined)
        query.set('page', String(route.page))
      if (route.name === 'feedbackReview') {
        if (route.horizon !== undefined) query.set('horizon', route.horizon)
        if (route.queuePage !== undefined) query.set('page', String(route.queuePage))
      }
      const path =
        route.name === 'feedback'
          ? '/feedbacks'
          : `/feedbacks/review/${encodeURIComponent(route.eventId)}`
      return `${path}${query.size === 0 ? '' : `?${query}`}`
    }
    case 'queue':
      return '/'
  }
}

/** The query parameters a verification link carries its finding in. */
const FINDING_PARAM = 'apontamento'
const NEAR_PREPARATION_PARAM = 'pote'
const NEAR_CHECK_PARAM = 'tipo'

function findingQuery(
  finding: string | undefined,
  near: { preparation?: string | undefined; check?: string | undefined } | undefined,
): string {
  const params = new URLSearchParams()
  if (finding !== undefined) params.set(FINDING_PARAM, finding)
  if (near?.preparation !== undefined) params.set(NEAR_PREPARATION_PARAM, near.preparation)
  if (near?.check !== undefined) params.set(NEAR_CHECK_PARAM, near.check)
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}

function verificationPath(at: VerificationAt): string {
  return at.kind === 'numbered' ? `/${at.branch}/${at.quotation}` : `/verifications/${at.id}`
}

/** A path segment that is a whole number, and nothing else. */
function asNumber(part: string | undefined): number | undefined {
  if (part === undefined || !/^\d+$/.test(part)) return undefined
  return Number(part)
}

/**
 * The quotation half, which may be negative.
 *
 * A simulation is written with branch 0 and a quotation number counting down
 * from -1, so that a dry run can never collide with a real ERP key. Requiring
 * digits on both halves made `/0/-1` fall through to the queue, so every
 * simulation became unreachable by address the moment the numbered form
 * arrived -- silently, because an unrecognised path lands on the queue rather
 * than a 404.
 *
 * Nothing produces such a link any more, since a simulation is addressed by
 * its uuid. This is here so that one already in somebody's history still opens.
 */
function asQuotationNumber(part: string | undefined): number | undefined {
  if (part === undefined || !/^-?\d+$/.test(part)) return undefined
  return Number(part)
}

export function parsePath(pathname: string, search = ''): Route {
  const parts = pathname.split('/').filter((part) => part !== '')
  const query = new URLSearchParams(search)
  const finding = query.get(FINDING_PARAM) ?? undefined
  const preparation = query.get(NEAR_PREPARATION_PARAM) ?? undefined
  const check = query.get(NEAR_CHECK_PARAM) ?? undefined
  const near = preparation === undefined && check === undefined ? undefined : { preparation, check }

  if (parts[0] === 'verifications' && parts[1] !== undefined) {
    const at = { kind: 'identified', id: parts[1] } as const
    return parts[2] === 'full' ? { name: 'full', at } : { name: 'verification', at, finding, near }
  }

  /*
   * `/6/833127` -- a verification at the root, which is where the two numbers
   * belong if they are to be short enough to say.
   *
   * Both segments must be whole numbers, which is what keeps this from
   * swallowing every other route: nothing else in this app has a numeric first
   * segment, so `/branches/12` and `/products/71230` cannot be mistaken for a
   * quotation. A named route that ever starts with digits would have to be
   * matched above this.
   */
  const branch = asNumber(parts[0])
  const quotation = asQuotationNumber(parts[1])
  if (branch !== undefined && quotation !== undefined) {
    const at = { kind: 'numbered', branch, quotation } as const
    return parts[2] === 'full' ? { name: 'full', at } : { name: 'verification', at, finding, near }
  }
  if (parts[0] === 'products') {
    return parts[1] === undefined
      ? { name: 'products' }
      : { name: 'product', erpCode: decodeURIComponent(parts[1]) }
  }
  if (parts[0] === 'synonyms') return { name: 'synonyms' }
  if (parts[0] === 'users') {
    if (parts[1] === undefined) return { name: 'users' }
    // `invite` is not a uuid, so it cannot be a member's id.
    if (parts[1] === 'invite') return { name: 'userInvite' }
    return { name: 'user', id: decodeURIComponent(parts[1]) }
  }
  if (parts[0] === 'roles') {
    if (parts[1] === undefined) return { name: 'roles' }
    if (parts[1] === 'new') return { name: 'role' }
    return { name: 'role', id: decodeURIComponent(parts[1]) }
  }
  if (parts[0] === 'jobs') return { name: 'jobs' }
  if (parts[0] === 'branches') {
    return parts[1] === undefined
      ? { name: 'branches' }
      : { name: 'branch', id: decodeURIComponent(parts[1]) }
  }
  if (parts[0] === 'accounts') {
    return parts[1] === undefined
      ? { name: 'accounts' }
      : { name: 'account', id: decodeURIComponent(parts[1]), tab: toAccountTab(parts[2]) }
  }
  // The rules had a screen of their own until #687; they are set on the
  // account's page now, so the old address lands on the accounts.
  if (parts[0] === 'rules') return { name: 'accounts' }
  if (parts[0] === 'forms') {
    // `equivalences` is no form id, so not a form's page; nor is `capsules`,
    // whose tab moved to the catalogue (#636) and whose old address lands there.
    if (parts[1] === undefined) return { name: 'forms' }
    if (parts[1] === 'equivalences') return { name: 'formEquivalences' }
    if (parts[1] === 'capsules') return { name: 'products' }
    return { name: 'form', id: decodeURIComponent(parts[1]) }
  }
  if (parts[0] === 'status') return { name: 'status' }
  if (parts[0] === 'ai') return { name: 'ai' }
  if (parts[0] === 'reports') return { name: 'reports', tab: toReportTab(parts[1]) }
  // Where the report lived before it became a tab; links to it were kept.
  if (parts[0] === 'conference-time') return { name: 'reports', tab: 'conference-time' }

  if (parts[0] === 'golden-set') {
    return parts[1] === undefined
      ? { name: 'goldenSet' }
      : { name: 'goldenCase', caseId: decodeURIComponent(parts[1]) }
  }
  if (parts[0] === 'resolution-set') {
    return parts[1] === undefined
      ? { name: 'resolutionSet' }
      : { name: 'resolutionCase', caseId: decodeURIComponent(parts[1]) }
  }
  if (parts[0] === 'feedbacks') {
    const filters = feedbackFilters(query)
    const page = Math.max(1, Number.parseInt(query.get('page') ?? '', 10) || 1)
    if (parts[1] === 'review' && parts[2] !== undefined) {
      return {
        name: 'feedbackReview',
        eventId: decodeURIComponent(parts[2]),
        ...(filters === undefined ? {} : { filters }),
        ...(query.has('horizon') ? { horizon: query.get('horizon')! } : {}),
        ...(query.has('page') ? { queuePage: page } : {}),
      }
    }
    // `/feedbacks/painel` was the counts' own screen; they live on the list now.
    return {
      name: 'feedback',
      ...(filters === undefined ? {} : { filters }),
      ...(query.has('page') ? { page } : {}),
    }
  }
  if (parts[0] === 'simulations') {
    return parts[1] === undefined
      ? { name: 'simulations' }
      : { name: 'simulation', uploadId: decodeURIComponent(parts[1]) }
  }

  // Anything unrecognised is the queue rather than a 404 screen. A stale or
  // mistyped link landing a pharmacist on their work is a better failure than
  // landing them on an apology.
  return { name: 'queue' }
}

/** The current route, kept in step with the back and forward buttons. */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    parsePath(window.location.pathname, window.location.search),
  )

  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname, window.location.search))
    window.addEventListener('popstate', onPop)
    // Pushing does not fire popstate, so `navigate` announces itself here.
    window.addEventListener(NAVIGATED, onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      window.removeEventListener(NAVIGATED, onPop)
    }
  }, [])

  return route
}

const NAVIGATED = 'rp:navigated'

export function navigate(route: Route, options: { replace?: boolean } = {}): void {
  const path = toPath(route)
  if (path === window.location.pathname + window.location.search) return

  if (options.replace === true) window.history.replaceState(null, '', path)
  else window.history.pushState(null, '', path)

  window.dispatchEvent(new Event(NAVIGATED))
}
